import '../ClientAIStudioPinturaStylesOverrides.sass'

import { AI_TOOLS, customizeToolbarButtons } from '../AIStudioUtils'
import { Box, Stack, Typography } from '@mui/material'
import { GRAY_1000, WHITE } from 'constants/styling/upbrandingTheme'
import {
  createDefaultColorOptions,
  createDefaultImageScrambler,
  getEditorDefaults
} from '@pqina/pintura'

import { AIToolsSection } from './AIToolsSection.component'
import { ActionPage } from 'components/common/ActionPage/ActionPage.component'
import { AnalyticsEvent } from 'utils/analytics'
import { ClientAIStudioFooter } from './ClientAIStudioFooter.module'
import { ClientAIStudioNavTabs } from './ClientAIStudioNavTabs.component'
import { FC } from 'react'
import { GenerateButton } from '../../common/GenerateButton'
import { GenerateButtonToolbarExtension } from './GenerateButtonToolbarExtension'
import { GenerateLoading } from '../../common/GenerateLoading'
import { ManualToolsSection } from './ManualToolsSection.component'
import { MaskDrawer } from './MaskDrawer/MaskDrawer.controller'
import Modal from 'components/common/Modals/Modal/Modal'
import { NonSubscribersUpsell } from './NonSubscribersUpsell.component'
import { PinturaEditor } from '@pqina/react-pintura'
import { UndoRedoPanel } from 'components/pages/AIStudioPhotoEditing/common/UndoRedoPanel'
import { ZoomPanel } from '../../common/ZoomPanel'
import { getPinturaLocale } from 'utils/helpers'
import i18n from 'translations/i18n'
import { useClientAIStudio } from './ClientAIStudio.context'
import { useMaskDrawer } from './MaskDrawer/MaskDrawer.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

const PINTURA_EDITOR_DEFAULTS = getEditorDefaults({
  stickers: ['😎', '🎉', '😄', '👍', '👎'],
})

/**
 * AI Studio photo editing controller for individual visual selected from Gallery/Property Gallery
 * This component provides a user interface for editing images using AI and manual tools.
 * It allows users to select AI tools or manual tools for image editing, and provides a Pintura editor for advanced image manipulation.
 * The old VisualPinturaEditingController is deprecated and should not be used after the migration to AI Studio.
 * @example <ClientAIStudioController />
 */
export const ClientAIStudioController: FC = () => {
  const { t } = useTranslation(['ai_studio_editing', 'common'])

  const {
    previewImage,
    sourceVisualFilename,
    assignmentId,
    handleProcessPinturaUploadImage,
    isProcessingImage,
    onExitStudio,
    editorRef,
    handleUpdate,
    selectedPinturaFeature,
    selectedAIFeature,
    logAIStudioEvent,
    setPinturaHistory,
    isConfirmationOpen,
    setIsConfirmationOpen,
    handleFromManualToolsToAITools,
    handleFromAIToolsToManualTools,
  } = useClientAIStudio()

  const { historyVersion } = useMaskDrawer()

  const { hasUserActiveSubscription } = useUserData()

  const isMagicEraserTool = selectedAIFeature === AI_TOOLS.AI_MAGIC_ERASER

  return (
    <ActionPage
      id={`ai-studio-photo-editing-assignment-${assignmentId}-img-${sourceVisualFilename}`}
      title={t('title')}
      onClose={onExitStudio}
      customNavigationTab={<ClientAIStudioNavTabs />}
      footerChildren={
        <ClientAIStudioFooter
          onClose={onExitStudio}
          onSave={(tag) => selectedPinturaFeature ? handleProcessPinturaUploadImage(tag, false) : undefined} //TODO implement AI tools save
          isProcessing={isProcessingImage}
          marginRight={4}
        />
      }
    >
      <div style={{ height: '100%', display: 'grid', gridTemplateColumns: '1fr 3fr', overflow: 'hidden' }}>

        {/* LEFT TOOLBAR/MENU WITH SUBSCRIPTION PANEL, AI AND MANUAL TOOLS */}
        <Box
          sx={{
            backgroundColor: WHITE,
            padding: 1.6,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            overflow: 'auto'
          }}
        >
          <Stack gap={2.4}>

            {/* SUBSCRIBERS ONLY SECTION */}
            {!hasUserActiveSubscription && (
              <NonSubscribersUpsell />
            )}

            {hasUserActiveSubscription && <>
              <AIToolsSection />
              <ManualToolsSection />
            </>
            }

          </Stack>
        </Box>

        {/* RIGHT PREVIEWS */}
        {!selectedPinturaFeature
          ? <Box
            sx={{
              backgroundColor: GRAY_1000,
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden', // Ensures the whole image is visible when portrait or landscape
            }}
          >
            {!!isMagicEraserTool &&
              <Stack
                position="relative"
                width="100%"
                height="100%"
                maxHeight="calc(100vh - 22rem)"
                alignItems="center"
                justifyContent="center"
              >
                <ZoomPanel
                  isPanningDisabled={true}
                  toolBarComponent={<UndoRedoPanel key={historyVersion} />}
                >
                  <MaskDrawer />
                </ZoomPanel>
              </Stack>
            }

            {!isMagicEraserTool &&
              <Stack
                position="relative"
                width="100%"
                height="100%"
                maxHeight="calc(100vh - 22rem)"
                alignItems="center"
                justifyContent="center"
              >
                <ZoomPanel>
                  <img
                    src={previewImage}
                    alt="loaded_visual_for_edit"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain', // Ensures the whole image is visible when portrait or landscape
                      objectPosition: 'center',
                      display: 'block',
                      margin: 'auto',
                    }} />
                </ZoomPanel>
              </Stack>
            }

            {/* Generate loader icon */}
            {isProcessingImage && <GenerateLoading />}

            {/* Generate button with custom component extension for MAGIC ERASER AI TOOL */}
            <GenerateButton customComponent={isMagicEraserTool ? <GenerateButtonToolbarExtension /> : null} />

          </Box>
          : <div style={{ height: '100%' }}>
            <PinturaEditor
              ref={editorRef}
              {...PINTURA_EDITOR_DEFAULTS}
              src={previewImage}
              onWritehistory={() => setPinturaHistory(editorRef.current?.editor.history.get())}
              onUndo={() => {
                setPinturaHistory(editorRef.current?.editor.history.get())
                logAIStudioEvent(AnalyticsEvent.PINTURA_AI_STUDIO_UNDO)
              }}
              onRedo={() => {
                setPinturaHistory(editorRef.current?.editor.history.get())
                logAIStudioEvent(AnalyticsEvent.PINTURA_AI_STUDIO_REDO)
              }}
              willRenderToolbar={customizeToolbarButtons}
              util={selectedPinturaFeature}
              enableUtils={false} // Disable utils to prevent showing the default toolbar, we render our own toolbar
              // @ts-ignore
              imageScrambler={createDefaultImageScrambler({
                scrambleAmount: 8,
                blurAmount: 20,
                dataSizeScalar: 8
              })}
              onUpdate={handleUpdate}
              cropSelectPresetFilter={'landscape'}
              cropSelectPresetOptions={[
                [undefined, 'Custom'],
                [1, 'Square'],
                // shown when cropSelectPresetFilter is set to 'landscape'
                [2 / 1, '2:1'],
                [3 / 2, '3:2'],
                [4 / 3, '4:3'],
                [5 / 4, '5:4'],
                [16 / 10, '16:10'],
                [16 / 9, '16:9'],
                // shown when cropSelectPresetFilter is set to 'portrait'
                [1 / 2, '1:2'],
                [2 / 3, '2:3'],
                [3 / 4, '3:4'],
                [4 / 5, '4:5'],
                [10 / 16, '10:16'],
                [9 / 16, '9:16'],
              ]}
              fillOptions={[
                // Using the default markup editor colors
                ...Object.values(createDefaultColorOptions()),
              ]}
              locale={getPinturaLocale(i18n.language)}
            />
          </div>
        }

      </div>

      <Modal
        isOpen={isConfirmationOpen}
        title={t('export_progress_title')}
        onClickOutside={() => setIsConfirmationOpen(false)}
        onClose={() => setIsConfirmationOpen(false)}
        footerContent={
          <ClientAIStudioFooter
            onClose={() => setIsConfirmationOpen(false)}
            onSave={(tag) => !!selectedAIFeature
              ? handleFromAIToolsToManualTools(tag)
              : handleFromManualToolsToAITools(tag)
            }
            isProcessing={isProcessingImage}
          />
        }
      >
        <Stack margin={1.6}>
          <Typography variant='text-md'>{t(!!selectedAIFeature ? 'export_progress_from_ai_tools' : 'export_progress_from_manual_tools')}</Typography>
        </Stack>
      </Modal>
    </ActionPage >
  )
}
